import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/AdminSidebar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { LogOut } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { toast } from "sonner";

export const Route = createFileRoute("/admin")({
  component: AdminLayout,
  head: () => ({
    meta: [
      { title: "Admin panel — Cerveza.International" },
      { name: "description", content: "Interní administrace katalogu podniků Cerveza.International." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function AdminLayout() {
  const { user, hasRole, loading, roles } = useAuth();
  const isAdmin = hasRole("admin");
  const isVacantyAdmin = hasRole("vacanty_admin");
  const isCatalogReviewer = hasRole("catalog_editor") || hasRole("catalog_reviewer");
  const canEnter = isAdmin || isVacantyAdmin || isCatalogReviewer;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await queryClient.cancelQueries();
      queryClient.clear();
      const { error } = await supabase.auth.signOut();
      if (error) {
        toast.error(error.message);
        setSigningOut(false);
        return;
      }
      navigate({ to: "/auth", replace: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Odhlášení selhalo");
      setSigningOut(false);
    }
  };

  useEffect(() => {
    if (!loading && !user) {
      navigate({ to: "/auth", search: { redirect: "/admin" } });
    }
  }, [loading, user, navigate]);

  // Vacanty admin se přesměruje do své jediné sekce, pokud je na rozcestníku.
  useEffect(() => {
    if (canEnter && !isAdmin && isVacantyAdmin) {
      const path = window.location.pathname;
      if (path === "/admin" || path === "/admin/") {
        navigate({ to: "/admin/vacanty", replace: true });
      }
    }
    if (canEnter && !isAdmin && !isVacantyAdmin && isCatalogReviewer) {
      const path = window.location.pathname;
      if (path === "/admin" || path === "/admin/") {
        navigate({ to: "/admin/overovani-adres", replace: true });
      }
    }
  }, [canEnter, isAdmin, isVacantyAdmin, isCatalogReviewer, navigate]);

  if (loading || !user) {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">
        Načítám…
      </div>
    );
  }

  if (!canEnter) {
    return (
      <div className="grid min-h-screen place-items-center p-6 text-center">
        <div className="max-w-md">
          <h1 className="font-serif text-2xl">Nemáte přístup</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Admin panel je dostupný pouze pro administrátory. Vaše role: {roles.join(", ") || "uživatel"}.
          </p>
          <Button className="mt-4" onClick={() => navigate({ to: "/" })}>
            Zpět na web
          </Button>
        </div>
      </div>
    );
  }

  const initials = (user.user_metadata?.full_name as string | undefined)
    ?.split(" ").map((n) => n[0]).slice(0, 2).join("") ?? user.email?.[0]?.toUpperCase() ?? "?";
  const roleLabel = roles.includes("admin")
    ? "Admin"
    : roles.includes("vacanty_admin")
      ? "Admin Vacantů"
      : roles.includes("moderator") ? "Moderátor" : "";

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-muted/30">
        <AdminSidebar />
        <div className="flex flex-1 flex-col">
          <header className="sticky top-0 z-10 flex h-14 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur">
            <SidebarTrigger />
            <BackButton />
            <div className="flex-1" />
            <Badge variant="secondary" className="hidden sm:inline-flex">Lovable Cloud</Badge>
            <div className="flex items-center gap-2">
              <div className="grid h-8 w-8 place-items-center rounded-full bg-primary text-xs font-semibold uppercase text-primary-foreground">
                {initials}
              </div>
              <div className="hidden text-right text-xs leading-tight sm:block">
                <div className="font-medium">{user.user_metadata?.full_name ?? user.email}</div>
                <div className="text-muted-foreground">{roleLabel}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={handleSignOut} disabled={signingOut} aria-label="Odhlásit" className="gap-1.5">
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">{signingOut ? "Odhlašuji…" : "Odhlásit"}</span>
              </Button>
            </div>
          </header>
          <div className="flex-1 p-4 sm:p-6">
            <Outlet />
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}
